import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import communityService from '../../../services/community/communityService';
import { store } from '../../../redux/store';
import { setSelectedCommunity, setSelectedDashboard } from '../../../redux/slices/applicationSlice';
import StepFlow, { StepFlowStep } from '../../components/misc/StepFlow';
import { SubscriptionType, AnySubscriptionType } from '../../../types/misc';
import { isSubscriptionGreaterThanOrEqual } from '../../../utils/subscriptionUtils';
import AreYouSureModal from '../../components/modals/AreYouSureModal';
import {
  Feature,
  CommunityFeatures,
  featuresInSubscription,
  getMinimumSubscriptionForFeatures,
} from '../../../utils/FeatureUtils';
import FeaturesForm from '../../components/misc/FeaturesForm';
import { SubscriptionSelector } from '../../components/selection-lists/SubscriptionSelector';

export default function ActivateDashboardPage() {
  const { community } = useLocation().state ?? {};
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<SubscriptionType>(
    SubscriptionType.FREE,
  );
  const [features, setFeatures] = useState<CommunityFeatures>({});
  const indexState = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!community) navigate('/');

    try {
      let initialFeatures: CommunityFeatures = {};

      if (community.wantedDashboardFeatures) {
        const wanted =
          typeof community.wantedDashboardFeatures === 'string'
            ? JSON.parse(community.wantedDashboardFeatures)
            : community.wantedDashboardFeatures;

        // Map DashboardFeatures -> Features
        if (wanted) {
          if (wanted.MEMBER_ADMINISTRATION) 
            initialFeatures[Feature.MEMBER_ADMINISTRATION] = true;
          if (wanted.PARTICIPATION_INSIGHTS)
            initialFeatures[Feature.PARTICIPATION_INSIGHTS] = true;
          if (wanted.COMMUNICATION) 
            initialFeatures[Feature.COMMUNICATION] = true;
          if (wanted.FINANCIAL_ADMINISTRATION)
            initialFeatures[Feature.FINANCIAL_ADMINISTRATION] = true;
        }
      }

      // Check if features is already an object or needs to be parsed
      if (typeof community.features === 'string') {
        setFeatures(JSON.parse(community.features) as CommunityFeatures);
      } else if (community.features && typeof community.features === 'object') {
        setFeatures(community.features as CommunityFeatures);
      } else {
        setFeatures({});
      }

      if (typeof community.features === 'string') {
        const parsed = JSON.parse(community.features) as CommunityFeatures;
        initialFeatures = { ...initialFeatures, ...parsed };
      } else if (community.features && typeof community.features === 'object') {
        initialFeatures = { ...initialFeatures, ...(community.features as CommunityFeatures) };
      }

      setFeatures(initialFeatures || {});

      const requiredSubscription = getMinimumSubscriptionForFeatures(initialFeatures || {});
      setSelectedSubscriptionType(requiredSubscription);
    } catch (e) {
      console.error('Failed to parse features:', e);
      setFeatures({});
      setSelectedSubscriptionType(SubscriptionType.FREE);
    }
  }, [community, navigate, setSelectedSubscriptionType]);

  if (!community) return <></>;

  const handleStep = (i: 1 | -1) => indexState[1](indexState[0] + i);

  const steps = [
    <FeaturesView
      setSelectedSubscriptionType={setSelectedSubscriptionType}
      features={features}
      setFeatures={setFeatures}
      setStep={handleStep}
    />,
    <SubscriptionStep
      setStep={handleStep}
      selectedSubscriptionType={selectedSubscriptionType}
      setSelectedSubscriptionType={setSelectedSubscriptionType}
      features={features}
      setFeatures={setFeatures}
    />,
  ];

  return <StepFlow indexState={indexState} steps={steps} />;
}

type FeaturesViewProps = {
  setSelectedSubscriptionType: (type: SubscriptionType) => void;
  features: CommunityFeatures;
  setFeatures: (features: CommunityFeatures) => void;
  setStep: (i: 1 | -1) => void;
};

function FeaturesView({
  setSelectedSubscriptionType,
  features,
  setFeatures,
  setStep,
}: FeaturesViewProps) {
  const { t } = useTranslation();
  const [warningOpen, setWarningOpen] = React.useState(false);
  const [deferredStepChange, setDeferredStepChange] = React.useState<null | 1 | -1>(null);
  const wrappedSetStep = (diff: 1 | -1) => {
    const anySelected = features && Object.values(features).some(Boolean);

    if (!anySelected && diff === 1) {
      setDeferredStepChange(diff);
      setWarningOpen(true);
    } else {
      setStep(diff);
    }
  };

  return (
    <>
      <StepFlowStep
        setStep={wrappedSetStep}
        showPrev={false}
        title={t('page.onboarding.communityFeaturesScreen.title')}
        subtitle={t('page.onboarding.communityFeaturesScreen.subtitle')}
        className="max-w-4xl">
        <FeaturesForm
          features={features}
          onFeaturesChange={(newFeature) => {
            const requiredSubscription = getMinimumSubscriptionForFeatures(newFeature);
            setFeatures(newFeature);
            setSelectedSubscriptionType(requiredSubscription);
          }}
        />
      </StepFlowStep>

      <AreYouSureModal
        open={warningOpen}
        setOpen={setWarningOpen}
        title={t('page.onboarding.communityFeaturesScreen.modal.title')}
        description={t('page.onboarding.communityFeaturesScreen.modal.description')}
        confirmLabel={t('page.onboarding.communityFeaturesScreen.modal.confirm')}
        cancelLabel={t('page.onboarding.communityFeaturesScreen.modal.cancel')}
        onConfirm={() => {
          setWarningOpen(false);
          if (deferredStepChange) {
            setStep(deferredStepChange);
            setDeferredStepChange(null);
          }
        }}
        onCancel={() => {
          setWarningOpen(false);
          setDeferredStepChange(null);
        }}
      />
    </>
  );
}

interface SubscriptionStepProps {
  setStep: (i: 1 | -1) => void;
  selectedSubscriptionType: SubscriptionType | null;
  setSelectedSubscriptionType: (type: SubscriptionType) => void;
  features: CommunityFeatures;
  setFeatures: (features: CommunityFeatures) => void;
}

function SubscriptionStep({
  setStep,
  selectedSubscriptionType,
  setSelectedSubscriptionType,
  features,
  setFeatures,
}: SubscriptionStepProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { community } = useLocation().state ?? {};
  const [downgradeWarningOpen, setDowngradeWarningOpen] = useState(false);

  const disableIncompatibleFeatures = () => {
    const allowedFeatures =
      featuresInSubscription[selectedSubscriptionType ?? SubscriptionType.FREE];
    const updatedFeatures = { ...features };
    Object.keys(updatedFeatures).forEach((key) => {
      if (!allowedFeatures[key as keyof typeof allowedFeatures]) {
        updatedFeatures[key as keyof typeof updatedFeatures] = false;
      }
    });
    return updatedFeatures;
  };

  const handleSubscriptionSelection = (subscriptionType: AnySubscriptionType) => {
    // This page is only for community subscriptions, so we can safely cast
    const communitySubscriptionType = subscriptionType as SubscriptionType;
    const requiredSubscription = getMinimumSubscriptionForFeatures(features || {});
    if (!isSubscriptionGreaterThanOrEqual(communitySubscriptionType, requiredSubscription)) {
      setSelectedSubscriptionType(communitySubscriptionType);
      setDowngradeWarningOpen(true);
    } else {
      setSelectedSubscriptionType(communitySubscriptionType);
    }
  };

  const handleConfirmDowngrade = () => {
    if (selectedSubscriptionType) {
      const updatedFeatures = disableIncompatibleFeatures();
      setSelectedSubscriptionType(selectedSubscriptionType);
      setFeatures(updatedFeatures);
    }

    setDowngradeWarningOpen(false);
  };

  const handleCancelDowngrade = () => {
    setDowngradeWarningOpen(false);
  };

  const handleSubmit = async () => {
    const updatedCommunity = await communityService.activateDashboard(
      community.id,
      features,
      selectedSubscriptionType || SubscriptionType.FREE,
    );

    store.dispatch(setSelectedCommunity(updatedCommunity.id));
    store.dispatch(setSelectedDashboard(updatedCommunity.dashboard!.dashboardId));

    navigate('/overview');
  };

  return (
    <>
      <StepFlowStep
        setStep={setStep}
        disabled={!selectedSubscriptionType}
        showNext={false}
        onSubmit={handleSubmit}
        title={t('page.onboarding.subscription.title')}
        subtitle={t('page.onboarding.subscription.subtitle')}
        className="max-w-6xl">
        <SubscriptionSelector
          selectedSubscription={selectedSubscriptionType}
          mode="selectable"
          onSubscriptionSelect={handleSubscriptionSelection}
          showTrialPeriod
          trialDays={30}
        />
      </StepFlowStep>

      <AreYouSureModal
        open={downgradeWarningOpen}
        setOpen={setDowngradeWarningOpen}
        title={t('page.onboarding.subscription.downgrade.title')}
        description={t('page.onboarding.subscription.downgrade.description')}
        confirmLabel={t('page.onboarding.subscription.downgrade.confirm')}
        cancelLabel={t('page.onboarding.subscription.downgrade.cancel')}
        onConfirm={handleConfirmDowngrade}
        onCancel={handleCancelDowngrade}
      />
    </>
  );
}
